import { Select } from "@bbri/ui";
import i18n from 'i18next';
import { Toast } from 'primereact/toast';
import React, { Component, createRef } from 'react';
import { withTranslation, WithTranslation } from "react-i18next";
import "./SelectLanguage.scss";
import { TranslationService } from '../../form/services/TranslationService';
import { SeverityEnum } from '../enum/SeverityEnum';

interface Props extends WithTranslation {}

interface State {
  languages: string[];
  selectedLanguage: string;
  isChangingLanguage: boolean;
}

class SelectLanguage extends Component<Props, State> {
  private translationService: TranslationService;
  private fallbackLng: string;
  private toast: React.RefObject<Toast>;

  constructor(props: Props) {
    super(props);

    this.translationService = new TranslationService();
    this.fallbackLng = i18n.options.fallbackLng as string;
    this.toast = createRef();

    this.state = {
      languages: [],
      selectedLanguage: localStorage.getItem('i18nextLng') || this.fallbackLng[0],
      isChangingLanguage: false,
    };
  }

  async componentDidMount() {
    try {
      const response = await this.translationService.getExistingLanguages();
      if (response.status !== 200) {
        throw new Error("Failed to fetch languages.");
      }

      let selectedLanguage = localStorage.getItem('i18nextLng') || this.fallbackLng[0];
      const languages = response.data;
      const url = new URL(window.location.href);
      let rawLocale = url.searchParams.get('locale');

      if (rawLocale) {
        if (!rawLocale?.includes("-")) {
          rawLocale = rawLocale.includes("nl") || rawLocale.includes("fr")
            ? rawLocale + "-be"
            : rawLocale = rawLocale + "-eu"
        }

        if (languages.includes(rawLocale)) {
          selectedLanguage = rawLocale;
          await i18n.changeLanguage(rawLocale);
        }
      }

      this.setState({ languages, selectedLanguage });
    } catch (error) {
      console.error("Error fetching languages: ", error);
    }
  }

  componentWillUnmount() {}

  async checkLanguageAvailability(language: string): Promise<boolean> {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/translations/${language}`);
      const data = await response.json();
      return !data.fallbackApplied;
    } catch (error) {
      console.error("Error checking language availability:", error);
      return false;
    }
  }

  async handleSelectChange(value: string) {
    if (this.state.isChangingLanguage) {
      return;
    }

    const shortLocale = value.split("-")[0];
    const url = new URL(window.location.href);

    url.searchParams.set('locale', shortLocale);
    window.history.pushState({}, '', url.toString());

    this.setState({ isChangingLanguage: true });

    try {
      const isLanguageAvailable = await this.checkLanguageAvailability(value);

      if (isLanguageAvailable) {
        this.setState({ selectedLanguage: value });
        await this.props.i18n.changeLanguage(value);
      }
      else {
        this.setState({ selectedLanguage: this.fallbackLng[0] });
        await this.props.i18n.changeLanguage(this.fallbackLng[0]);

        document.dispatchEvent(
          new CustomEvent(
            'languageChanged',
            { detail: this.fallbackLng[0] }
          )
        );

        (this.toast.current as Toast)?.show({
          severity: SeverityEnum.ERROR,
          detail: `The requested language is not available. The content will be displayed in the default language.`,
        });
      }
    } catch (error) {
      console.error("Error during language change:", error);
      this.setState({ selectedLanguage: this.fallbackLng[0] });

      await this.props.i18n.changeLanguage(this.fallbackLng[0]);

      (this.toast.current as Toast)?.show({
        severity: SeverityEnum.ERROR,
        detail: `An error occurred. The content will be displayed in the default language.`,
      });
    } finally {
      this.setState({ isChangingLanguage: false });
    }
  }

  render() {
    const { languages, selectedLanguage } = this.state;

    const options: { id: string; name: string }[] = languages.map((language) => ({
      id: language,
      name: language.split("-")[0].toUpperCase(),
    }));

    return (
      <div className="select-language">
        <Toast ref={this.toast} />
        <Select
          selection={{
            id: selectedLanguage,
            name: selectedLanguage.split("-")[0].toUpperCase(),
          }}
          displayProperty="name"
          options={options}
          onChange={(item: { id: string; name: string }) =>
            this.handleSelectChange(item.id)
          }
        />
      </div>
    );
  }
}

export default withTranslation()(SelectLanguage);
